import {useCallback, useEffect, useRef, useState} from "react";
import {Tag, Tooltip} from "antd";
import {FontAwesomeIcon} from "~/components";
import {useLookupKeywordMetricsMutation} from "~/reduxs/api/keywordApiSlice";

/**
 * Volume/CPC/KD từ DataForSEO (§8) — helper dùng chung cho Khám phá / Kho từ khoá.
 *
 * BE đọc cache chung xuyên user trước (0đ, không quota); từ khoá cache miss được XẾP HÀNG
 * (tick gộp lô gọi DFS, kết quả về sau vài phút) → hook poll lại endpoint mỗi POLL_MS khi
 * còn dòng 'pending' (endpoint idempotent — poll không trừ quota thêm).
 */

const POLL_MS = 5000;
const MAX_POLLS = 60; // ~5 phút — task standard queue của DFS thường xong trong tầm này

/** Hiển thị volume gọn: 1.2k / 35k / 1,2tr. */
export const volumeText = (v) => {
	if (v === null || v === undefined) return '—';
	if (v >= 1000000) return `${(v / 1000000).toLocaleString('vi-VN', {maximumFractionDigits: 1})}tr`;
	if (v >= 1000) return `${(v / 1000).toLocaleString('vi-VN', {maximumFractionDigits: 1})}k`;
	return v.toLocaleString('vi-VN');
};

/** Màu mức độ khó (KD 0-100): xanh dễ → đỏ khó. */
const kdColor = (kd) => (kd < 30 ? 'green' : kd < 60 ? 'orange' : 'red');

const dash = <span style={{color: '#9ca3af'}}>—</span>;

/**
 * 3 cột Volume / CPC / KD cho antd Table.
 * @param resolve (record) => {metrics, pending} — metrics ưu tiên kết quả tra mới, fallback
 *                cache BE đã join sẵn vào row; pending = đang chờ tick xử lý.
 */
export const metricsColumns = (resolve) => [
	{
		title: <Tooltip title="Lượt tìm kiếm trung bình / tháng (theo dữ liệu Google Ads)">Volume</Tooltip>,
		key: 'dfs_volume', align: 'center', width: 100,
		sorter: (a, b) => (resolve(a).metrics?.volume ?? -1) - (resolve(b).metrics?.volume ?? -1),
		render: (_, r) => {
			const {metrics, pending} = resolve(r);
			if (pending) return <span style={{color: '#9ca3af'}}><FontAwesomeIcon icon="fa-light fa-spinner" className="fa-spin" /></span>;
			if (!metrics) return dash;
			return metrics.volume === null ? <Tooltip title="Google không có dữ liệu volume cho từ này">{dash}</Tooltip> : <strong>{volumeText(metrics.volume)}</strong>;
		},
	},
	{
		title: <Tooltip title="Giá thầu trung bình mỗi click (USD, Google Ads)">CPC</Tooltip>,
		key: 'dfs_cpc', align: 'center', width: 90, responsive: ['md'],
		sorter: (a, b) => (resolve(a).metrics?.cpc ?? -1) - (resolve(b).metrics?.cpc ?? -1),
		render: (_, r) => {
			const {metrics, pending} = resolve(r);
			if (pending || !metrics || metrics.cpc === null) return dash;
			return `$${Number(metrics.cpc).toFixed(2)}`;
		},
	},
	{
		title: <Tooltip title="Độ khó SEO 0-100 — càng cao càng khó lên top">KD</Tooltip>,
		key: 'dfs_kd', align: 'center', width: 80,
		sorter: (a, b) => (resolve(a).metrics?.kd ?? -1) - (resolve(b).metrics?.kd ?? -1),
		render: (_, r) => {
			const {metrics, pending} = resolve(r);
			if (pending || !metrics || metrics.kd === null) return dash;
			return <Tag color={kdColor(metrics.kd)} style={{marginInlineEnd: 0}}>{metrics.kd}</Tag>;
		},
	},
];

/**
 * Hook tra volume/CPC/KD: `run(keywords)` gọi API rồi tự poll tới khi hết pending.
 * Trả `rows` (map keyword → {status, metrics}) để merge vào bảng + `lookingUp` + `lastQuota`.
 */
export function useMetricsLookup(projectId, notification) {
	const [lookup] = useLookupKeywordMetricsMutation();
	const [rows, setRows] = useState({});
	const [lookingUp, setLookingUp] = useState(false);
	const timerRef = useRef(null);
	const pollsRef = useRef(0);
	const aliveRef = useRef(true);

	useEffect(() => () => {
		aliveRef.current = false;
		if (timerRef.current) clearTimeout(timerRef.current);
	}, []);

	const poll = useCallback(async (keywords, isFirst) => {
		let data;
		try {
			data = await lookup({projectId, keywords}).unwrap();
		} catch (e) {
			if (isFirst) notification?.error({message: 'Lỗi', description: e?.data?.message || 'Không tra được volume từ khoá.'});
			setLookingUp(false);
			return;
		}

		if (!aliveRef.current || !data) return;

		setRows((prev) => {
			const next = {...prev};
			(data.rows || []).forEach((row) => { next[row.keyword] = row; });
			return next;
		});

		if (isFirst) {
			if (!data.available) {
				notification?.warning({message: 'Chưa sẵn sàng', description: 'Hệ thống chưa cấu hình nguồn dữ liệu volume. Liên hệ quản trị viên.'});
			} else if (!data.can_lookup) {
				notification?.warning({message: 'Gói không hỗ trợ', description: 'Gói hiện tại không có lượt tra volume/CPC/KD. Vui lòng nâng cấp gói.'});
			} else {
				const quotaBlocked = (data.rows || []).filter((r) => r.status === 'quota').length;
				if (quotaBlocked > 0) {
					notification?.warning({
						message: 'Hết lượt tra trong chu kỳ',
						description: `${quotaBlocked} từ khoá chưa tra được vì hết hạn mức (đã dùng ${data.quota?.used ?? '?'}/${data.quota?.limit ?? '?'} lượt). Hạn mức làm mới ${data.quota?.reset_at || 'khi bạn gia hạn gói'}.`,
					});
				}
			}
		}

		const pendingKeywords = (data.rows || []).filter((r) => r.status === 'pending').map((r) => r.keyword);

		if (pendingKeywords.length > 0 && pollsRef.current < MAX_POLLS) {
			pollsRef.current += 1;
			timerRef.current = setTimeout(() => poll(pendingKeywords, false), POLL_MS);
		} else {
			if (pendingKeywords.length > 0) {
				notification?.info({message: 'Đang xử lý nền', description: 'Một số từ khoá vẫn đang được tra — quay lại tab này sau ít phút để xem kết quả.'});
			}
			setLookingUp(false);
		}
	}, [lookup, projectId, notification]);

	const run = useCallback((keywords) => {
		const list = [...new Set(keywords.map((k) => String(k || '').trim()).filter(Boolean))].slice(0, 200);
		if (!list.length || lookingUp) return;
		pollsRef.current = 0;
		setLookingUp(true);
		poll(list, true);
	}, [poll, lookingUp]);

	return {run, rows, lookingUp};
}
